import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { forwardRef, useCallback, useMemo } from 'react';
import type { ReleaseTest } from '../data/release-tests-data';
import type { TestResultData } from '../hooks/useTestResults';
import { downloadCsv, generateFilteredCsv } from '../utils/csv-export';
import { TestFeatureCard } from './TestFeatureCard';
import { VersionBadge } from './VersionBadge';

interface VersionTestGroupProps {
  releaseTest: ReleaseTest;
  results: TestResultData[];
  isExpanded: boolean;
  onToggle: () => void;
  onNavigateToChangelog: (version: string) => void;
}

/**
 * Convert a version string like "4.2.1" to a URL-safe hash fragment "4-2-1".
 * Used for deep-linking into a specific release's test group.
 */
function versionToHash(version: string): string {
  return version.replace(/\./g, '-');
}

export const VersionTestGroup = forwardRef<HTMLDivElement, VersionTestGroupProps>(
  function VersionTestGroup(
    { releaseTest, results, isExpanded, onToggle, onNavigateToChangelog },
    ref
  ) {
    const { t } = useTranslation();

    // Single-pass aggregation over `results` plus a feature-index lookup map.
    // Without this, each feature card triggered an O(n) `.find()` on every
    // render — O(n²) over a version group of ~50 features + ~50 results.
    const { testedCount, totalCount, progressPct, resultsByFeatureIndex } = useMemo(() => {
      let tested = 0;
      const byIdx = new Map<number, TestResultData>();
      for (const r of results) {
        if (r.status === 'passed' || r.status === 'failed') tested++;
        byIdx.set(r.featureIndex, r);
      }
      const total = releaseTest.features.length;
      return {
        testedCount: tested,
        totalCount: total,
        progressPct: total > 0 ? Math.round((tested / total) * 100) : 0,
        resultsByFeatureIndex: byIdx,
      };
    }, [results, releaseTest.features.length]);

    const handleExport = useCallback(() => {
      const csv = generateFilteredCsv([releaseTest], results);
      const date = new Date().toISOString().split('T')[0];
      downloadCsv(csv, `test-results-v${releaseTest.version}-${date}.csv`);
    }, [releaseTest, results]);

    return (
      <div ref={ref} id={`testing-v${versionToHash(releaseTest.version)}`}>
        {/* Version header */}
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-3.5 text-left transition-colors hover:bg-accent/50"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <Badge className="bg-accent text-foreground">v{releaseTest.version}</Badge>
            <span className="text-sm text-muted-foreground">{releaseTest.date}</span>
            <VersionBadge
              label={t('releaseHub.testing.changelogBadge')}
              version={releaseTest.version}
              onClick={onNavigateToChangelog}
            />
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span
              role="button"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation();
                handleExport();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  handleExport();
                }
              }}
              className="flex items-center gap-1 rounded px-2 py-1 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label={t('releaseHub.testing.exportCsv')}
            >
              <Download className="h-3.5 w-3.5" />
              {t('releaseHub.testing.exportCsv')}
            </span>
            <span>
              {t('releaseHub.testing.progress', { done: testedCount, total: totalCount })}
            </span>
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-green-500 transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </button>

        {/* Cards container */}
        {isExpanded && (
          <div className="mt-1 space-y-2.5 rounded-b-lg border border-t-0 border-border bg-card p-3">
            {releaseTest.features.map((feature, fIdx) => (
              <TestFeatureCard
                key={fIdx}
                feature={feature}
                featureIndex={fIdx}
                version={releaseTest.version}
                existingResult={resultsByFeatureIndex.get(fIdx)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);
